import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuViewport,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ChevronDown, Menu, X, Coins, LogOut, User } from "lucide-react";

const PRODUCT_ITEMS = [
  { name: "Email Validation", href: "/product/email-validation" },
  { name: "Bulk Validation", href: "/product/bulk-validation" },
  { name: "API Access", href: "/product/api" },
  { name: "Chrome Extension", href: "/product/extension" },
];

const SOLUTIONS_ITEMS = [
  { name: "Marketing Teams", href: "/solutions/marketing" },
  { name: "Saas", href: "/solutions/saas" },
  { name: "Agencies", href: "/solutions/agencies" },
  { name: "CRMs", href: "/solutions/crms" },
];

const INTEGRATIONS_ITEMS = [
  { name: "Zapier", href: "/integrations/zapier" },
  { name: "HubSpot", href: "/integrations/hubspot" },
  { name: "Salesforce", href: "/integrations/salesforce" },
  { name: "Mailchimp", href: "/integrations/mailchimp" },
];

export default function Navbar({ user, isAuthenticated }) {
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useNavigate();

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleLogin = () => {
    navigate("/signin");
  };

  const handleStartFree = () => {
    if (isAuthenticated) {
      navigate("/pricing");
    } else {
      navigate("/signin");
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    window.location.reload();
  };

  return (
    <header className="w-full bg-white dark:bg-slate-900/60 border-b border-slate-100 dark:border-slate-800 sticky top-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* LEFT: Logo + Brand */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                className="text-sky-600"
              >
                <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.08" />
                <path d="M7 12a5 5 0 0110 0v1a5 5 0 01-10 0v-1z" fill="currentColor" />
              </svg> */}
              <img src="https://cdn-icons-png.flaticon.com/128/12795/12795772.png"  className="h-9"/>
              <span className="text-xl font-semibold text-sky-500 dark:text-slate-100">
                ValidMail
              </span>
            </Link>
          </div>

          {/* RIGHT: Nav + Actions */}
          <div className="flex items-center gap-4">
            <nav className="hidden md:block">
              <NavigationMenu className="relative z-10">
                <NavigationMenuList className="flex items-center gap-1">
                  {/* Product (with dropdown) */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 data-[state=open]:bg-slate-50 dark:data-[state=open]:bg-slate-800">
                      Product
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-80 grid-cols-2 gap-2 p-4">
                        {PRODUCT_ITEMS.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="block rounded-md px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Solutions */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 data-[state=open]:bg-slate-50 dark:data-[state=open]:bg-slate-800">
                      Solutions
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-80 grid-cols-2 gap-2 p-4">
                        {SOLUTIONS_ITEMS.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="block rounded-md px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Pricing */}
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/pricing"
                        className="inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        Pricing
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  {/* Integrations */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 data-[state=open]:bg-slate-50 dark:data-[state=open]:bg-slate-800">
                      Integrations
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-80 grid-cols-2 gap-2 p-4">
                        {INTEGRATIONS_ITEMS.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="block rounded-md px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Resources */}
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/resources"
                        className="inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        Resources
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  {/* Company */}
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/company"
                        className="inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        Company
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
                <NavigationMenuViewport className="absolute left-0 top-full mt-1.5 w-full" />
              </NavigationMenu>
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {/* Credits Badge */}
                  <Link
                    to="/pricing"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-colors"
                  >
                    <Coins className="h-4 w-4" />
                    <span className="text-sm font-semibold">
                      {user?.credits || 0}
                    </span>
                  </Link>

                  {/* Profile Dropdown */}
                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger className="bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 data-[state=open]:bg-slate-50 dark:data-[state=open]:bg-slate-800">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              {user?.name?.split(" ")[0] || "Account"}
                            </span>
                          </div>
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="w-48 p-2">
                            <li>
                              <NavigationMenuLink asChild>
                                <Link
                                  to="/profile"
                                  className="block rounded-md px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                                >
                                  Profile
                                </Link>
                              </NavigationMenuLink>
                            </li>
                            <li>
                              <NavigationMenuLink asChild>
                                <Link
                                  to="/homie"
                                  className="block rounded-md px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                                >
                                  Dashboard
                                </Link>
                              </NavigationMenuLink>
                            </li>
                            <li>
                              <button
                                onClick={handleSignOut}
                                className="w-full text-left flex items-center gap-2 rounded-md px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-red-600 dark:text-red-400"
                              >
                                <LogOut className="h-4 w-4" />
                                Sign out
                              </button>
                            </li>
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </>
              ) : (
                <>
                  <button
                    onClick={handleLogin}
                    className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Log in
                  </button>

                  <button
                    onClick={handleStartFree}
                    className="px-6 py-2 rounded-full text-sm font-medium bg-sky-500 text-white hover:bg-sky-600 transition-colors shadow-sm"
                  >
                    Start free
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800">
                    <Menu className="h-6 w-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[320px] p-0">
                  <div className="p-4 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                    <Link
                      to="/"
                      className="flex items-center gap-2"
                      onClick={() => setOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        className="text-sky-600"
                      >
                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.08" />
                        <path
                          d="M7 12a5 5 0 0110 0v1a5 5 0 01-10 0v-1z"
                          fill="currentColor"
                        />
                      </svg>
                      <span className="text-lg font-semibold">ValidMail</span>
                    </Link>

                    <button
                      onClick={() => setOpen(false)}
                      className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {/* <X className="h-5 w-5" /> */}
                    </button>
                  </div>

                  <ScrollArea className="h-[calc(100vh-160px)]">
                    <div className="p-4 space-y-1">
                      {/* Show user info if authenticated */}
                      {isAuthenticated && user && (
                        <div className="mb-4 p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {user.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sky-700 dark:text-sky-400">
                            <Coins className="h-4 w-4" />
                            <span className="text-sm font-semibold">{user.credits || 0} credits</span>
                          </div>
                        </div>
                      )}

                      {/* Product Section */}
                      <div>
                        <button
                          onClick={() => toggleSection("product")}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Product
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform",
                              expandedSections.product && "rotate-180"
                            )}
                          />
                        </button>
                        {expandedSections.product && (
                          <div className="mt-1 ml-3 space-y-1">
                            {PRODUCT_ITEMS.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Solutions Section */}
                      <div>
                        <button
                          onClick={() => toggleSection("solutions")}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Solutions
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform",
                              expandedSections.solutions && "rotate-180"
                            )}
                          />
                        </button>
                        {expandedSections.solutions && (
                          <div className="mt-1 ml-3 space-y-1">
                            {SOLUTIONS_ITEMS.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pricing */}
                      <Link
                        to="/pricing"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Pricing
                      </Link>

                      {/* Integrations Section */}
                      <div>
                        <button
                          onClick={() => toggleSection("integrations")}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Integrations
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform",
                              expandedSections.integrations && "rotate-180"
                            )}
                          />
                        </button>
                        {expandedSections.integrations && (
                          <div className="mt-1 ml-3 space-y-1">
                            {INTEGRATIONS_ITEMS.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Resources */}
                      <Link
                        to="/resources"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Resources
                      </Link>

                      {/* Company */}
                      <Link
                        to="/company"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Company
                      </Link>

                      {/* Authenticated Mobile Links */}
                      {isAuthenticated && (
                        <>
                          <Link
                            to="/profile"
                            onClick={() => setOpen(false)}
                            className="block px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            Profile
                          </Link>
                          <Link
                            to="/"
                            onClick={() => setOpen(false)}
                            className="block px-3 py-2 rounded-md font-medium text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            Dashboard
                          </Link>
                        </>
                      )}
                    </div>
                  </ScrollArea>

                  <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    {isAuthenticated ? (
                      <button
                        onClick={() => {
                          setOpen(false);
                          handleSignOut();
                        }}
                        className="w-full px-4 py-2 rounded-full border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-2"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setOpen(false);
                            handleLogin();
                          }}
                          className="w-full px-4 py-2 mb-2 rounded-full border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Log in
                        </button>
                        <button
                          onClick={() => {
                            setOpen(false);
                            handleStartFree();
                          }}
                          className="w-full px-4 py-2 rounded-full bg-sky-500 text-white font-medium hover:bg-sky-600 shadow-sm"
                        >
                          Start free
                        </button>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
